import { Button, Flex, HStack, IconButton, Input, Td, Tr } from "@chakra-ui/react";
import { Check, X } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { ACTIONS_SLOT_WIDTH, limeFocus, UNIT_SEGMENT_WIDTH, VALUE_FIELD_WIDTH } from "./fieldStyles";

const EMPTY = { category: "", dataKey: "", dataValue: "", unit: "", scope: "" };

/**
 * Inline form for adding a metric by hand.
 *
 * The server forces `origin: USER` on anything created here regardless of what is sent, so a hand-typed
 * row stays distinguishable from extracted output — which is what makes extraction quality measurable
 * after the fact.
 *
 * `defaultKey` / `defaultUnit` prefill the row when it is opened from a GAP in the target structure — the
 * datapoint's own name and expected unit. Retyping them by hand would be the surest way to produce a name
 * that does not match the datapoint the user is trying to fill.
 *
 * `compact` lays the same fields across one wide cell (matching the main page's 3-column table, Datapoint/
 * Value/Actions) instead of one `Td` per field (the 8-column full table used by `ExtractionReviewModal`
 * and the no-structure fallback) — see the "two column modes" note in frontend/CLAUDE.md. The field set
 * itself is unchanged either way: Applies to is still collected on add even though the compact READ row
 * hides it behind the info icon.
 */
export default function AddMetricRow({
  defaultCategory,
  defaultKey,
  defaultUnit,
  onAdd,
  onCancel,
  isAdding,
  compact = false,
  onEditingChange,
}) {
  useEffect(() => {
    if (!onEditingChange) return undefined;
    onEditingChange("add-metric", true);
    return () => onEditingChange("add-metric", false);
  }, [onEditingChange]);
  const [draft, setDraft] = useState({
    ...EMPTY,
    category: defaultCategory ?? "",
    dataKey: defaultKey ?? "",
    unit: defaultUnit ?? "",
  });
  const set = (field) => (event) => setDraft((prev) => ({ ...prev, [field]: event.target.value }));
  const canSubmit = draft.dataKey.trim().length > 0;

  const actions = (
    <HStack spacing={1} justify="flex-end">
      <Button
        size="xs"
        variant="primary"
        leftIcon={<Check size={12} />}
        isLoading={isAdding}
        isDisabled={!canSubmit}
        onClick={() => onAdd(draft, () => setDraft({ ...EMPTY, category: defaultCategory ?? "" }))}
      >
        Add
      </Button>
      <IconButton size="xs" variant="secondary" aria-label="Cancel" icon={<X size={12} />} onClick={onCancel} />
    </HStack>
  );

  if (compact) {
    /*
      Laid out exactly as the compact EDIT row (EditableMetricRow): the name as plain row text, the
      lime-outlined Value+Unit field, and the lime tick / cancel icons — so adding a custom metric looks like
      editing any other row rather than like a separate form. The name is a borderless input at row-text
      size; "Applies to" has no column in compact mode, so it sits under the name as a smaller grey line,
      the way a filled row stacks its detail under the name.
    */
    const submit = () => {
      if (!canSubmit || isAdding) return;
      onAdd(draft, () => setDraft({ ...EMPTY, category: defaultCategory ?? "" }));
    };
    const keys = (e) => {
      if (e.key === "Enter") submit();
      if (e.key === "Escape") onCancel?.();
    };
    return (
      <Tr bg="limeShade.50">
        <Td>
          <Flex direction="column" gap="2px">
            <Input
              autoFocus
              variant="unstyled"
              fontSize="14px"
              placeholder="Metric name"
              _placeholder={{ color: "gray.400" }}
              value={draft.dataKey}
              onChange={set("dataKey")}
              onKeyDown={keys}
            />
            <Input
              variant="unstyled"
              fontSize="12px"
              color="gray.600"
              placeholder="Applies to (optional)"
              _placeholder={{ color: "gray.400" }}
              value={draft.scope}
              onChange={set("scope")}
              onKeyDown={keys}
            />
          </Flex>
        </Td>
        <Td>
          <Flex justify="flex-end">
            {/* The edit row's own field: same fixed width, lime border and focus ring. */}
            <Flex
              border="1.5px solid"
              borderColor="lime"
              borderRadius="8px"
              overflow="hidden"
              bg="white"
              boxShadow="0 0 0 3px rgba(133,194,42,.18)"
              flexShrink={0}
              w={VALUE_FIELD_WIDTH}
            >
              <Input
                variant="unstyled"
                flex="1"
                minW={0}
                px="10px"
                py="7px"
                textAlign="right"
                fontSize="14px"
                fontWeight="600"
                sx={{ fontVariantNumeric: "tabular-nums" }}
                placeholder="Value"
                value={draft.dataValue}
                onChange={set("dataValue")}
                onKeyDown={keys}
              />
              <Input
                variant="unstyled"
                w={UNIT_SEGMENT_WIDTH}
                flexShrink={0}
                px={2}
                textAlign="center"
                fontSize="12.5px"
                fontWeight="600"
                color="gray.600"
                bg="gray.50"
                borderLeft="1px solid"
                borderColor="gray.200"
                borderRadius={0}
                placeholder="Unit"
                value={draft.unit}
                onChange={set("unit")}
                onKeyDown={keys}
              />
            </Flex>
          </Flex>
        </Td>
        {/* Its own column, same fixed width every row shape uses — see ACTIONS_SLOT_WIDTH's comment in
            fieldStyles.js. */}
        <Td w={ACTIONS_SLOT_WIDTH}>
          <Flex justify="flex-end" align="center" gap={2}>
            <IconButton
              size="xs"
              bg="lime"
              color="darkTeal"
              _hover={{ bg: "limeShade.600" }}
              aria-label="Add"
              icon={<Check size={14} weight="bold" />}
              isLoading={isAdding}
              isDisabled={!canSubmit}
              onClick={submit}
            />
            <IconButton size="xs" variant="secondary" aria-label="Cancel" icon={<X size={12} />} onClick={onCancel} />
          </Flex>
        </Td>
      </Tr>
    );
  }

  return (
    <Tr bg="limeShade.50">
      <Td>
        <Input size="xs" placeholder="Metric *" value={draft.dataKey} onChange={set("dataKey")} {...limeFocus} />
      </Td>
      <Td>
        <Input size="xs" placeholder="Value" value={draft.dataValue} onChange={set("dataValue")} textAlign="right" {...limeFocus} />
      </Td>
      <Td>
        <Input size="xs" placeholder="Unit" value={draft.unit} onChange={set("unit")} {...limeFocus} />
      </Td>
      <Td>
        <Input size="xs" placeholder="Applies to" value={draft.scope} onChange={set("scope")} {...limeFocus} />
      </Td>
      <Td colSpan={4}>{actions}</Td>
    </Tr>
  );
}
